import { PropertyResponseDto } from "@/common/api/datasources/Api";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  Building2,
  Calendar,
  DollarSign,
  Image as ImageIcon,
  MapPin,
  Maximize2,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface PropertyHeaderProps {
  property: PropertyResponseDto;
  onBack?: () => void;
}

export const PropertyHeader = ({
  property,
  onBack
}: PropertyHeaderProps) => {
  const { t } = useTranslation();
  const [isImageOpen, setIsImageOpen] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "available":
        return "bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200";
      case "occupied":
        return "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200";
      case "sold":
        return "bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200";
      case "maintenance":
        return "bg-red-100 text-red-800 border-red-200 hover:bg-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200";
    }
  };

  const formatPrice = (price?: number) => {
    if (!price) return t("common.notSpecified");
    return new Intl.NumberFormat('uk-UA', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <>
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="flex flex-col md:flex-row">
            {/* Зображення */}
            <div
              className={cn(
                "relative overflow-hidden cursor-pointer group",
                "h-[240px] md:h-auto w-full md:w-[320px] md:min-h-[280px]"
              )}
              onClick={() => property.images?.length && setIsImageOpen(true)}
              title={t("properties.images.viewFullSize")}
            >
              {property.images && property.images[0] ? (
                <>
                  <img
                    src={property.images[0].fullSize.url}
                    alt={property.name}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center p-4">
                    <div className="flex items-center gap-2 text-white">
                      <Maximize2 className="h-5 w-5" />
                      <span className="text-sm font-medium">{t("properties.images.viewFullSize")}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full w-full">
                  <ImageIcon className="h-12 w-12 mb-2 opacity-30" />
                  <span className="text-sm opacity-70">{t("properties.images.noImage")}</span>
                </div>
              )}
            </div>

            {/* Інформація */}
            <div className="flex-1 p-6 md:p-8">
              <div className="space-y-6">
                <div>
                  <div className="flex items-start justify-between">
                    <div>
                      <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Building2 className="h-6 w-6 text-primary" />
                        {property.name}
                      </h1>
                      <p className="mt-1 opacity-70">{t("properties.description")}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant="secondary"
                        className={cn(
                          getStatusColor(property.status),
                          "px-3 py-1.5 font-medium border shadow-sm rounded-md",
                          "transition-colors duration-200"
                        )}
                      >
                        {t(`properties.status.${property.status}`)}
                      </Badge>
                    </div>
                  </div>

                  <Badge
                    variant="outline"
                    className="px-3 py-1.5 font-medium border shadow-sm transition-colors duration-200 rounded-md mt-3"
                  >
                    {t(`properties.type.${property.type}`)}
                  </Badge>
                </div>

                <Separator />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/10">
                      <MapPin className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm opacity-70">{t("properties.details.address")}</p>
                      <p className="font-medium truncate" title={property.address || t("common.notSpecified")}>
                        {property.address || t("common.notSpecified")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/10">
                      <Calendar className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm opacity-70">{t("properties.details.createdAt")}</p>
                      <p className="font-medium" title={new Date(property.createdAt).toLocaleDateString()}>
                        {new Date(property.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/10">
                      <DollarSign className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm opacity-70">{t("properties.details.price")}</p>
                      <p className="font-medium" title={formatPrice(property.price)}>
                        {formatPrice(property.price)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isImageOpen} onOpenChange={setIsImageOpen}>
        <DialogContent className="max-w-5xl p-0 rounded-lg overflow-hidden border-0">
          {property.images && property.images[0] && (
            <img
              src={property.images[0].fullSize.url}
              alt={property.name}
              className="w-full h-auto"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}; 